import React, { useState, useEffect, useCallback } from 'react';
import {
    Card,
    Table,
    Space,
    Button,
    Tag,
    Typography,
    Spin,
    Alert,
    Segmented,
    Empty,
    Tooltip,
    ConfigProvider
} from 'antd';
import {
    ReloadOutlined,
    FileSearchOutlined,
    WarningOutlined,
    ExclamationCircleOutlined,
    RollbackOutlined,
    CheckCircleOutlined,
    LinkOutlined
} from '@ant-design/icons';
import ruRU from 'antd/es/locale/ru_RU';
import moment from 'moment';
import 'moment/locale/ru';
import { fetchAuditLog } from './api';
import { changePageTitle } from '../../../utils/title';
import './AuditLog.css';

moment.locale('ru');

const { Title, Text, Paragraph } = Typography;

// Конфигурация типов событий
const EVENT_TYPES = {
    all: { label: 'Все', value: null, icon: null, color: null },
    missing: {
        label: '🟠 Пропущенные',
        value: 'missing',
        icon: <WarningOutlined />,
        color: 'orange',
        tagLabel: 'Пропущено'
    },
    amount_mismatch: {
        label: '🟡 Разница сумм',
        value: 'amount_mismatch',
        icon: <ExclamationCircleOutlined />,
        color: 'gold',
        tagLabel: 'Разница сумм'
    },
    reversed: {
        label: '⚫ Откаты',
        value: 'reversed',
        icon: <RollbackOutlined />,
        color: 'default',
        tagLabel: 'Откат'
    },
};

// Маппинг действий на русский язык
const ACTION_TRANSLATIONS = {
    'Created missing sale fact': 'Создан пропущенный факт продажи',
    'Updated amount mismatch': 'Исправлена разница сумм',
    'Amount updated': 'Сумма обновлена',
    'Reversed sale fact': 'Откат факта продажи',
    'Created missing qualified fact': 'Создан пропущенный факт квалификации',
    'Created missing measure fact': 'Создан пропущенный факт замера',
    'Sale fact reversed': 'Факт продажи отменён',
    'Missing sale created': 'Создана пропущенная продажа',
};

// Функция перевода действия
const translateAction = (action) => {
    if (!action) return '—';
    return ACTION_TRANSLATIONS[action] || action;
};

const AuditLogPage = ({ userPermission }) => {
    const [logs, setLogs] = useState([]);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState(null);
    const [limit, setLimit] = useState(100);

    useEffect(() => {
        changePageTitle('Лог сверки данных');
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const data = await fetchAuditLog({ limit, type: filter });
            setLogs(data.logs || []);
            setCount(data.count || 0);
        } catch (err) {
            console.error('Ошибка загрузки audit log:', err);
            setError(err?.message || 'Не удалось загрузить данные. Попробуйте позже.');
        } finally {
            setLoading(false);
        }
    }, [limit, filter]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleFilterChange = (value) => {
        setFilter(value === 'all' ? null : value);
        setLimit(100); // Сброс лимита при смене фильтра
    };

    const handleLoadMore = () => {
        setLimit((prev) => Math.min(prev + 100, 500));
    };

    // Форматирование валюты
    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '—';
        return new Intl.NumberFormat('ru-RU', {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount) + ' ₸';
    };

    // Форматирование даты
    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return moment(dateStr).format('DD.MM.YYYY HH:mm');
    };

    const formatShortDate = (dateStr) => {
        if (!dateStr) return '—';
        return moment(dateStr).format('DD.MM.YYYY');
    };

    // Получение конфигурации типа
    const getTypeConfig = (type) => {
        return EVENT_TYPES[type] || { tagLabel: type, color: 'default' };
    };

    // Колонки таблицы
    const columns = [
        {
            title: 'Дата',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            render: (val) => (
                <Text type="secondary" style={{ fontSize: 13 }}>
                    {formatDate(val)}
                </Text>
            ),
            sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
            defaultSortOrder: 'descend',
        },
        {
            title: 'Тип',
            dataIndex: 'type',
            key: 'type',
            width: 140,
            render: (type) => {
                const config = getTypeConfig(type);
                return (
                    <Tag
                        color={config.color}
                        icon={config.icon}
                        style={{ minWidth: 100, textAlign: 'center' }}
                    >
                        {config.tagLabel}
                    </Tag>
                );
            },
            filters: [
                { text: 'Пропущено', value: 'missing' },
                { text: 'Разница сумм', value: 'amount_mismatch' },
                { text: 'Откат', value: 'reversed' },
            ],
            onFilter: (value, record) => record.type === value,
        },
        {
            title: 'Клиент',
            key: 'clientName',
            width: 200,
            ellipsis: true,
            render: (_, record) => {
                const clientName = record.clientName || record.details?.clientName;
                const clientId = record.clientId || record.details?.clientId;

                if (!clientName) return <Text type="secondary">—</Text>;

                // Если есть clientId - делаем ссылку на страницу клиента
                if (clientId) {
                    return (
                        <Tooltip title={`Открыть карточку: ${clientName}`}>
                            <a
                                href={`/client/${clientId}`}
                                className="audit-log-client-link"
                                onClick={(e) => {
                                    e.preventDefault();
                                    window.location.href = `/client/${clientId}`;
                                }}
                            >
                                {clientName}
                            </a>
                        </Tooltip>
                    );
                }

                // Если clientId нет - просто текст
                return (
                    <Tooltip title={clientName}>
                        <Text>{clientName}</Text>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Сделка',
            dataIndex: 'amoLeadId',
            key: 'amoLeadId',
            width: 140,
            render: (id) => {
                if (!id) return '—';
                const url = `https://secretdoorskz.amocrm.ru/leads/detail/${id}`;
                return (
                    <Tooltip title="Открыть в AmoCRM">
                        <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="audit-log-link"
                        >
                            <LinkOutlined style={{ marginRight: 4 }} />
                            {id}
                        </a>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Сумма',
            key: 'amount',
            width: 140,
            render: (_, record) => {
                const amount = record.details?.amount;
                return (
                    <Text strong style={{ color: amount > 0 ? '#52c41a' : undefined }}>
                        {formatCurrency(amount)}
                    </Text>
                );
            },
            sorter: (a, b) => (a.details?.amount || 0) - (b.details?.amount || 0),
        },
        {
            title: 'Действие',
            dataIndex: 'action',
            key: 'action',
            ellipsis: true,
            render: (action) => {
                const translated = translateAction(action);
                return (
                    <Tooltip title={translated}>
                        <Text>{translated}</Text>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Дата закрытия',
            key: 'closedAt',
            width: 120,
            render: (_, record) => {
                const closedAt = record.details?.closedAt;
                return (
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        {formatShortDate(closedAt)}
                    </Text>
                );
            },
        },
    ];

    // Фильтр-опции для Segmented
    const filterOptions = [
        { label: 'Все', value: 'all' },
        { label: '🟠 Пропущенные', value: 'missing' },
        { label: '🟡 Разница сумм', value: 'amount_mismatch' },
        { label: '⚫ Откаты', value: 'reversed' },
    ];

    // Рендер контента
    const renderContent = () => {
        if (error) {
            return (
                <Alert
                    message="Ошибка загрузки"
                    description={error}
                    type="error"
                    showIcon
                    action={
                        <Button size="small" type="primary" onClick={loadData}>
                            Повторить
                        </Button>
                    }
                    style={{ marginBottom: 16 }}
                />
            );
        }

        if (!loading && logs.length === 0) {
            return (
                <Empty
                    image={<CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a' }} />}
                    imageStyle={{ height: 80 }}
                    description={
                        <Space direction="vertical" size={4}>
                            <Text strong style={{ fontSize: 16 }}>✨ Расхождений не обнаружено</Text>
                            <Text type="secondary">Все данные синхронизированы с AmoCRM</Text>
                        </Space>
                    }
                />
            );
        }

        return (
            <>
                <Table
                    dataSource={logs}
                    columns={columns}
                    rowKey={(record) => record._id || record.id || `${record.amoLeadId}-${record.createdAt}`}
                    loading={loading}
                    pagination={false}
                    size="middle"
                    scroll={{ x: 900 }}
                    className="audit-log-table"
                />

                {logs.length > 0 && logs.length >= limit && limit < 500 && (
                    <div style={{ textAlign: 'center', marginTop: 16 }}>
                        <Button
                            onClick={handleLoadMore}
                            loading={loading}
                            type="default"
                        >
                            Загрузить ещё
                        </Button>
                    </div>
                )}
            </>
        );
    };

    return (
        <ConfigProvider locale={ruRU}>
            <div className="audit-log-page">
                {/* Заголовок */}
                <Card bordered={false} className="audit-log-header">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                            <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <FileSearchOutlined /> Лог сверки данных
                            </Title>
                            <Paragraph type="secondary" style={{ margin: '8px 0 0', fontSize: 14 }}>
                                История исправлений, внесённых автоматической сверкой с AmoCRM
                            </Paragraph>
                        </div>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={loadData}
                            loading={loading}
                        >
                            Обновить
                        </Button>
                    </div>
                </Card>

                {/* Фильтры */}
                <Card bordered={false} className="audit-log-filters">
                    <Space direction="vertical" style={{ width: '100%' }} size={12}>
                        <Text strong>Тип события:</Text>
                        <Segmented
                            options={filterOptions}
                            value={filter || 'all'}
                            onChange={handleFilterChange}
                            size="large"
                        />
                        {count > 0 && (
                            <Text type="secondary">
                                Найдено записей: <Text strong>{count}</Text>
                            </Text>
                        )}
                    </Space>
                </Card>

                {/* Таблица */}
                <Card bordered={false} className="audit-log-content">
                    <Spin spinning={loading && logs.length === 0}>
                        {renderContent()}
                    </Spin>
                </Card>
            </div>
        </ConfigProvider>
    );
};

export default AuditLogPage;
